// =============================================================================
// AppErrorBoundary — top-level safety net for unhandled render errors.
// =============================================================================
// Without this, an exception in any component during render unmounts the
// whole tree and leaves a blank page. With this, the boundary catches the
// error, shows what happened, and offers a "reset local state and reload"
// button — which fixes the most common cause: corrupted persisted Zustand
// state (e.g. references to a deleted server, malformed migrated values).
//
// The reset removes our app's localStorage entry but keeps the auth token
// so the user doesn't have to log in again. If reset still doesn't fix it,
// they can try "Log out fully" which also clears the token.
// =============================================================================

import { Component, type ErrorInfo, type ReactNode } from 'react';

interface Props {
  children: ReactNode;
}

interface State {
  error: Error | null;
  componentStack: string | null;
}

export class AppErrorBoundary extends Component<Props, State> {
  state: State = { error: null, componentStack: null };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // Log so it shows up in the browser console for the user (and us, if
    // they share a screenshot).
    // eslint-disable-next-line no-console
    console.error('Unhandled render error:', error, info);
    this.setState({ componentStack: info.componentStack ?? null });
  }

  resetPersistedState = () => {
    // Wipe the Zustand persist key. Keep the auth token so the user stays
    // logged in. If they need a full reset, the second button handles it.
    try {
      localStorage.removeItem('chatapp-app');
    } catch {
      /* localStorage may be disabled — ignore */
    }
    window.location.reload();
  };

  fullReset = () => {
    try {
      localStorage.clear();
      sessionStorage.clear();
    } catch {
      /* ignore */
    }
    window.location.reload();
  };

  reload = () => {
    window.location.reload();
  };

  render() {
    if (!this.state.error) return this.props.children;
    return (
      <div
        style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '2rem',
          fontFamily: 'system-ui, sans-serif',
          background: '#1a1a1a',
          color: '#e6e6e6',
        }}
      >
        <div
          style={{
            maxWidth: 640,
            width: '100%',
            background: '#252525',
            border: '1px solid #444',
            borderRadius: 8,
            padding: 24,
          }}
        >
          <h1 style={{ marginTop: 0, fontSize: 20, color: '#ff8888' }}>
            Something went wrong
          </h1>
          <p style={{ fontSize: 14, opacity: 0.85 }}>
            The app hit an error it couldn't recover from. This is most often
            caused by stale local data (e.g. a reference to a server or
            channel that no longer exists). Resetting local state usually
            fixes it without losing your account.
          </p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 16 }}>
            <button
              onClick={this.resetPersistedState}
              style={{
                background: '#3b82f6',
                color: 'white',
                border: 0,
                padding: '8px 14px',
                borderRadius: 6,
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              Reset local state &amp; reload
            </button>
            <button
              onClick={this.reload}
              style={{
                background: '#444',
                color: 'white',
                border: 0,
                padding: '8px 14px',
                borderRadius: 6,
                cursor: 'pointer',
              }}
            >
              Just reload
            </button>
            <button
              onClick={this.fullReset}
              style={{
                background: 'transparent',
                color: '#ff8888',
                border: '1px solid #663333',
                padding: '8px 14px',
                borderRadius: 6,
                cursor: 'pointer',
              }}
            >
              Log out fully
            </button>
          </div>
          <details style={{ marginTop: 18, fontSize: 12, opacity: 0.75 }}>
            <summary style={{ cursor: 'pointer' }}>Technical details</summary>
            <div style={{ marginTop: 8 }}>
              <strong>Error:</strong>{' '}
              <code style={{ color: '#ffaaaa' }}>
                {this.state.error.name}: {this.state.error.message}
              </code>
            </div>
            {this.state.error.stack && (
              <pre
                style={{
                  marginTop: 6,
                  whiteSpace: 'pre-wrap',
                  background: '#1a1a1a',
                  padding: 8,
                  borderRadius: 4,
                  maxHeight: 200,
                  overflow: 'auto',
                  fontSize: 11,
                }}
              >
                {this.state.error.stack}
              </pre>
            )}
            {this.state.componentStack && (
              <pre
                style={{
                  marginTop: 6,
                  whiteSpace: 'pre-wrap',
                  background: '#1a1a1a',
                  padding: 8,
                  borderRadius: 4,
                  maxHeight: 200,
                  overflow: 'auto',
                  fontSize: 11,
                }}
              >
                {this.state.componentStack}
              </pre>
            )}
          </details>
        </div>
      </div>
    );
  }
}
